import React, { useEffect, useState } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import AdminDataTable from "../../components/common/AdminDataTable";
import { transactionHistoryColumns } from "../../tables/transactionHistoryColumn";
import { can } from "../../utils/can";
import toast from "../../utils/toast";
import { useLocation } from "react-router-dom";

const getHistoryQueryFilters = (search) => {
  const params = new URLSearchParams(search);

  return {
    filter: params.get("filter") || "",
    status: params.get("status") || "",
    fromDate: params.get("fromDate") || "",
    toDate: params.get("toDate") || ""
  };
};

const getTransactionPageTitle = (filter) => {
  if (filter === "dailyNewPaidMembers") {
    return "Today New Paid Members";
  }

  if (filter === "weeklyNewPaidMembers") {
    return "Weekly Paid Members";
  }

  if (filter === "dailyRevenue") {
    return "Today Total Revenue";
  }

  if (filter === "weeklyRevenue") {
    return "Weekly Total Revenue";
  }

  return "Transaction History";
};

export default function TransactionHistory() {
  const location = useLocation();
  const initialQueryFilters = getHistoryQueryFilters(location.search);
  const reportFilter = initialQueryFilters.filter;

  const PAGE_SIZE = 10;

  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [pageTitle, setPageTitle] = useState(getTransactionPageTitle(initialQueryFilters.filter));

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  /* 🔍 Filters */
  const [searchBy, setSearchBy] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialQueryFilters.status);
  const [fromDate, setFromDate] = useState(initialQueryFilters.fromDate);
  const [toDate, setToDate] = useState(initialQueryFilters.toDate);
  /* Applied values — only update on Submit / Clear / URL change */
  const [appliedFromDate, setAppliedFromDate] = useState(initialQueryFilters.fromDate);
  const [appliedToDate, setAppliedToDate] = useState(initialQueryFilters.toDate);

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";

  /* =============== EXCEL EXPORT =============== */
  const exportToExcel = async () => {
    try {
      setListLoading(true);
      
      // Fetch all data with current filters (no pagination limit)
      const query = new URLSearchParams({
        page: 1,
        pageSize: 10000, // Fetch all records
        filter: reportFilter,
        sortBy,
        sortOrder,
        searchBy,
        searchText,
        status: statusFilter,
        fromDate,
        toDate,
      }).toString();

      const response = await fetch(`${API_URL}/api/admin/transaction-history?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      const data = result.data || [];

      if (data.length === 0) {
        toast.showError("No data to export");
        setListLoading(false);
        return;
      }

      // Prepare CSV content
      const headers = [
        "ID",
        "Member ID",
        "Member Name",
        "Plan Name",
        "Transaction ID",
        "Invoice No",
        "Amount",
        "Status",
        "Date",
      ];

      const rows = data.map((row, index) => [
         index + 1,
        row.user?.profile_id || "-",
        row?.user?.name || "-",
        row?.plan?.name || "-",
        row?.transaction_id || "-",
        row?.invoice_no || "-",
        row?.price || "-",
        row?.status || "-",
        row?.createdAt
          ? new Date(row.createdAt).toLocaleDateString("en-GB")
          : "-",
      ]);

      // Convert to CSV
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((cell) =>
              typeof cell === "string" && cell.includes(",")
                ? `"${cell}"`
                : cell
            )
            .join(",")
        ),
      ].join("\n");

      // Create Blob and download
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `transaction-history-${new Date().getTime()}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.showSuccess(`Exported ${data.length} records to Excel`);
    } catch (error) {
      console.error("Export error:", error);
      toast.showError("Failed to export data");
    } finally {
      setListLoading(false);
    }
  };

   /* ---------------- ACTIONS ---------------- */
  
  const handleRenew = async (row) => {
    if(can("renew_member", "edit") === false) {
      toast.showError("You don't have permission to renew memberships.");
      return;
    }
    const confirmed = window.confirm(
      "Are you sure you want to renew this membership?"
    );

    if (!confirmed) return;

  try {
    const userId = row._id;
    const planId = row.userPlan.plan_id;
    const historyId = row._id;

    const response = await fetch(`${API_URL}/api/admin/attribute/renew-member`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        userId,
        planId,
        historyId
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Renew failed");
    }

    console.log("Renew Success:", data);
    toast.showSuccess(data.message);
    fetchHistory(); // Refresh list after renewal

  } catch (error) {
    console.error("Renew Error:", error);
    toast.showError(error.message);
  }
};

  const handleCancelled = async (row, status) => {
    if(can("renew_member", "delete") === false) {
      toast.showError(`You don't have permission to ${status.toLowerCase()} memberships.`);
      return;
    }
    
  try {

     const confirmed = window.confirm(
      `Are you sure you want to ${status.toLowerCase()} this membership?`
    );

    if (!confirmed) return;
   
    
    const userId = row.user_id;
    const planId = row.plan_id;
    const historyId = row._id;

    const response = await fetch(`${API_URL}/api/admin/attribute/cancelled-membership`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        userId,
        planId,
        historyId,
        status
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Cancel failed");
    }

    console.log("Cancel Success:", data);
    toast.showSuccess(data.message);
    fetchHistory(); // Refresh list after cancellation

  } catch (error) {
    console.error("Cancel Error:", error);
    toast.showError(error.message);
  }
};

  const columns = transactionHistoryColumns({
    PAGE_SIZE: pageSize,
    handleCancelled,
    handleRenew
  });

 

  
  const fetchHistory = async () => {
    try {
      setListLoading(true);

      const query = new URLSearchParams({
        page,
        pageSize,
        filter: reportFilter,
        sortBy,
        sortOrder,
        searchBy,
        searchText,
        status: statusFilter,
        fromDate: appliedFromDate,
        toDate: appliedToDate,
      }).toString();

      const response = await fetch(`${API_URL}/api/admin/transaction-history?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (result?.status) {
        setHistory(result.data || []);
        setTotalCount(result.totalCount || 0);
        setTotalRevenue(result.totalRevenue || 0);
        setTotalPages(result.totalPages || 1);
      } else {
        setHistory([]);
        setTotalCount(0);
        setTotalRevenue(0);
      }

    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    const nextFilters = getHistoryQueryFilters(location.search);

    setPageTitle(getTransactionPageTitle(nextFilters.filter));
    setStatusFilter(nextFilters.status);
    setFromDate(nextFilters.fromDate);
    setToDate(nextFilters.toDate);
    setAppliedFromDate(nextFilters.fromDate);
    setAppliedToDate(nextFilters.toDate);
    setPage(1);
  }, [location.search]);

  const handleFilterSubmit = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setPage(1);
    fetchHistory();
  };

  const handleClearFilter = () => {
    setSearchText("");
    setFromDate("");
    setToDate("");
    setAppliedFromDate("");
    setAppliedToDate("");
    setStatusFilter("");
    setPage(1);
    fetchHistory();
  };

  /* 🔄 Auto fetch on change — searchText is intentionally excluded;
     it only fires when pagination / sort / redirect-filter changes. */
  useEffect(() => {
    if(can("transaction_history", "view") === false){
      window.location.href = "/admin/dashboard";
      toast.showError("You don't have permission to view transaction history.");  
      return;
    }

    fetchHistory();
  }, [page, pageSize, sortBy, sortOrder, statusFilter, appliedFromDate, appliedToDate, reportFilter]);

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Transaction History" />

        <div className="right_col" role="main">
        

          <div className="x_panel">
            <div className="x_title">
              <h2>List</h2>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              <div className="filtercont">
                <div className="row">
                  <h4 style={{ fontSize: "14px", color: "#e013ba", marginTop: "8px" }}>
                Total Transactions: <strong>({totalCount})</strong>
                {" "}
                Total Revenue: <strong>₹ {totalRevenue}</strong>
              </h4>
                  <div className="col-lg-10">
                    <div className="row">

                      {/* <div className="col-sm-2">
                        <label>Search By</label>
                        <select
                          className="form-control"
                          value={searchBy}
                          onChange={(e) => setSearchBy(e.target.value)}
                        >
                          <option value="">Select</option>
                          <option value="member_id">Member Id</option>
                          <option value="transaction_id">Transaction Id</option>
                          <option value="invoice_no">Invoice Number</option>
                        </select>
                      </div> */}

                      <div className="col-sm-4">
                        <label>Search Text</label>
                        <input
                          type="text"
                          placeholder="Profile ID, Name, Email"
                          className="form-control"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleFilterSubmit(); }}
                        />
                      </div>

                      <div className="col-sm-2">
                        <label>From Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>

                      <div className="col-sm-2">
                        <label>To Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>

                      <div className="col-12 col-sm-2">
                        <label>&nbsp;</label>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-default submit sitebtn px-3 text-center"
                            onClick={handleFilterSubmit}
                          >
                            Submit
                          </button>
                         
                         
                          <button
                            className="btn btn-default submit sitebtn px-3 text-center"
                            style={{ backgroundColor: "#6b6463", borderColor: "#4f4a49", color: "#fff", marginLeft: "15px" }}
                            onClick={handleClearFilter}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-12 text-right"><hr /></div>
              <div className="col-lg-12 text-right">
                <div>
                  <button
                    className="btn btn-default submit sitebtn"
                    style={{
                      backgroundColor: "#2e9337",
                      borderColor: "#2e9337",
                      display: "inline-block",
                    }}
                    onClick={exportToExcel}
                    disabled={listLoading}
                  >
                    <i className="fa fa-file-excel-o"></i>&nbsp;&nbsp;
                    {listLoading ? "Exporting..." : "Export To Excel"}
                  </button>
                </div>
              </div>

              {/* 📊 Table */}
              <AdminDataTable
                columns={columns}
                data={history}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={listLoading}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
